import React from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, AlertTriangle, AlertOctagon, Eye, Users, CalendarDays, BarChart3,
} from 'lucide-react';
import { THRESHOLDS } from '../../config/dashboardConstants';
import {
  getOTGaugeStatus, getComplianceRingColor, getAlertSeverityStyle,
} from '../../config/dashboardHelpers';

const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0h';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

const MOMComplianceSection = ({
  overtimeStats, overtimeAlerts, overtimeLoading,
  restDayStats, restDayLoading,
}) => (
  <>
    {/* 72-Hour Overtime Monitoring */}
    <section className="space-y-6" aria-label="MOM Compliance Monitoring Dashboard">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="space-y-2 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0">72-Hour Overtime Monitoring</h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            MOM Compliance • Monthly overtime limit: 72 hours per employee
            {overtimeLoading && (
              <span className="ml-2 text-gray-500 italic">Computing…</span>
            )}
            {!overtimeLoading && overtimeAlerts?.length > 0 && (
              <span className="ml-2 text-red-600 font-medium">
                ⚠️ {overtimeAlerts.length} active alert{overtimeAlerts.length > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>

        {overtimeLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
                <div className="flex items-center">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
                  <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                    <div className="h-5 sm:h-6 w-10 sm:w-12 bg-gray-200 rounded" />
                    <div className="h-3 sm:h-4 w-20 sm:w-24 bg-gray-200 rounded" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="h-3 w-16 sm:w-20 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : !overtimeStats ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm font-medium text-yellow-800">
              Overtime monitoring data is temporarily unavailable. Please refresh the dashboard.
            </p>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {/* Employees Tracked */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  {overtimeStats?.currentMonth?.totalEmployees || 0}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Employees Tracked</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 truncate">
                {new Date().toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Action Required */}
          {(() => {
            const exceeded = overtimeStats?.exceededCount || 0;
            const critical = overtimeStats?.criticalCount || 0;
            const total = exceeded + critical;
            const parts = [
              exceeded > 0 && `${exceeded} exceeded`,
              critical > 0 && `${critical} critical`
            ].filter(Boolean).join(' · ') || 'All clear';
            return (
              <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertOctagon className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
                  </div>
                  <div className="ml-2 sm:ml-4 min-w-0">
                    <p className={`text-lg sm:text-2xl font-bold truncate ${total > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {total}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">Action Required</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 truncate">{parts}</p>
                </div>
              </div>
            );
          })()}

          {/* Watch List */}
          {(() => {
            const atRisk = overtimeStats?.atRiskCount || 0;
            const projected = overtimeStats?.projectedExceedCount || 0;
            const watch = overtimeStats?.watchCount || 0;
            const total = atRisk + projected + watch;
            const parts = [
              atRisk > 0 && `${atRisk} at risk`,
              projected > 0 && `${projected} projected`,
              watch > 0 && `${watch} watch`
            ].filter(Boolean).join(' · ') || 'All clear';
            return (
              <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                  <div className="ml-2 sm:ml-4 min-w-0">
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{total}</p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">Watch List</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 truncate">{parts}</p>
                </div>
              </div>
            );
          })()}

          {/* OT Summary */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                {/* Mobile/tablet: only Total OT (primary metric); avg moves to footer */}
                <div className="lg:hidden min-w-0">
                  <p className="text-lg font-bold text-gray-900 truncate">
                    {formatHoursMinutes(overtimeStats?.currentMonth?.totalOvertimeHours || 0)}
                  </p>
                  <p className="text-xs text-gray-600 truncate">Total OT</p>
                </div>
                {/* Desktop: dual-stat side-by-side */}
                <div className="hidden lg:flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0 min-w-0">
                    <p className="text-xl xl:text-2xl font-bold text-gray-900 whitespace-nowrap">
                      {formatHoursMinutes(overtimeStats?.currentMonth?.totalOvertimeHours || 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">Total OT</p>
                  </div>
                  <div className="w-px h-10 bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xl xl:text-2xl font-bold text-gray-900 truncate">
                      {formatHoursMinutes(overtimeStats?.currentMonth?.averageOvertimeHours || 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">Avg OT/Worker</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 truncate">
                <span className="lg:hidden">{formatHoursMinutes(overtimeStats?.currentMonth?.averageOvertimeHours || 0)} avg/worker</span>
                <span className="hidden lg:inline">OT Summary</span>
              </p>
            </div>
          </div>
        </div>

        {/* OT Utilization Gauge */}
        {overtimeStats?.currentMonth?.averageOvertimeHours != null && (
          <div className="mt-6 bg-gray-50 rounded-xl border p-5">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Average OT Utilization (72hr Limit)</h3>
              <span className="text-xs text-gray-400">
                {(overtimeStats.currentMonth.averageOvertimeHours || 0).toFixed(1)}h / 72h per worker
              </span>
            </div>
            {(() => {
              const avg = overtimeStats.currentMonth.averageOvertimeHours || 0;
              const pct = Math.min((avg / THRESHOLDS.OT_MONTHLY_LIMIT) * 100, 100);
              const gaugeStatus = getOTGaugeStatus(pct);
              return (
                <div>
                  <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
                    <div
                      className={`${gaugeStatus.barColor} h-4 rounded-full transition-all duration-700 relative`}
                      style={{ width: `${Math.max(pct, 1)}%` }}
                    >
                      {pct >= 15 && (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">
                          {pct.toFixed(0)}%
                        </span>
                      )}
                    </div>
                    <div className="absolute top-0 bottom-0 left-[60%] w-px bg-gray-400 opacity-40" title="60% threshold" />
                    <div className="absolute top-0 bottom-0 left-[80%] w-px bg-gray-400 opacity-40" title="80% threshold" />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[10px] sm:text-xs text-gray-400">
                    <span>0h</span>
                    <span className="hidden sm:inline">43h (60%)</span>
                    <span className="sm:hidden">60%</span>
                    <span className="hidden sm:inline">58h (80%)</span>
                    <span className="sm:hidden">80%</span>
                    <span>72h</span>
                  </div>
                  <p className={`text-xs font-medium mt-2 ${gaugeStatus.textColor}`}>
                    {gaugeStatus.label}
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Active Alerts List */}
        {overtimeAlerts?.length > 0 && (
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h3>
            <div className="space-y-3">
              {overtimeAlerts.slice(0, 5).map((alert, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getAlertSeverityStyle(alert.severity).dot}`} aria-hidden="true" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {alert.employeeName} ({alert.employeeId})
                      </p>
                      <p className="text-xs text-gray-600">
                        {alert.currentHours}h of 72h limit • {alert.monthYear}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAlertSeverityStyle(alert.severity).badge}`}>
                      {getAlertSeverityStyle(alert.severity).label}
                    </span>
                  </div>
                </div>
              ))}
              <div className="text-center pt-2 border-t border-gray-100">
                <div className="flex items-center justify-center space-x-4 pt-4">
                  <Link
                    to="/admin/attendance/overtime-monitoring"
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View Full Dashboard
                  </Link>
                  {overtimeAlerts.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {overtimeAlerts.length} active alert{overtimeAlerts.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </section>

    {/* Rest Day Interval Monitoring */}
    <section className="space-y-6" aria-label="Rest Day Interval Monitoring Dashboard">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="space-y-2 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0">Rest Day Interval Monitoring</h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-600">
            MOM Employment Act s36 • Maximum 12 consecutive working days between rest days
            {restDayStats?.activeAlertsCount > 0 && (
              <span className="ml-2 text-red-600 font-medium">
                ⚠️ {restDayStats.activeAlertsCount} active alert{restDayStats.activeAlertsCount > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {/* Employees Tracked */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{restDayStats?.totalMonitored || 0}</p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Employees Tracked</p>
              </div>
            </div>
          </div>

          {/* Action Required */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                {(() => {
                  const violated = restDayStats?.nonCompliantCount || 0;
                  const critical = restDayStats?.criticalCount || 0;
                  const total = violated + critical;
                  const parts = [
                    violated > 0 && `${violated} violated`,
                    critical > 0 && `${critical} critical`,
                  ].filter(Boolean);
                  return (
                    <>
                      <p className={`text-lg sm:text-2xl font-bold truncate ${total > 0 ? 'text-red-600' : 'text-gray-900'}`}>{total}</p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">Action Required</p>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {parts.length > 0 ? parts.join(' · ') : 'All clear'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Watch List */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-start">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Eye className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                {(() => {
                  const warning = restDayStats?.warningCount || 0;
                  const watch = restDayStats?.watchCount || 0;
                  const total = warning + watch;
                  const parts = [
                    warning > 0 && `${warning} warning`,
                    watch > 0 && `${watch} watch`,
                  ].filter(Boolean);
                  return (
                    <>
                      <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{total}</p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">Watch List</p>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {parts.length > 0 ? parts.join(' · ') : 'All clear'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Compliance Rate Ring */}
          {(() => {
            const rate = restDayStats?.complianceRate || 0;
            const compliant = restDayStats?.legallyCompliantCount ?? restDayStats?.totalMonitored ?? 0;
            const total = restDayStats?.totalMonitored || 0;
            const ringColor = getComplianceRingColor(rate);
            const circumference = 2 * Math.PI * 38;
            const offset = circumference - (rate / 100) * circumference;
            return (
              <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="relative w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 88 88">
                      <circle cx="44" cy="44" r="38" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                      <circle
                        cx="44" cy="44" r="38" fill="none"
                        stroke={ringColor} strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        className="transition-all duration-700"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900">{rate}%</span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">Compliance Rate</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{compliant} of {total} within limit</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  </>
);

export default MOMComplianceSection;
